/**
 * LandingPage Component
 * Modern SAAS-style landing page with Microsoft sign-in
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../hooks/useAuth';
import { isAuthenticated } from '../services/authService';
import { msalInstance, initializeMsal } from '../config/authConfig';
import Header from '../components/landing/Header';
import Hero from '../components/landing/Hero';
import Features from '../components/landing/Features';
import CallToAction from '../components/landing/CallToAction';
import Footer from '../components/landing/Footer';
import AuthButton from '../components/auth/AuthButton';
import AuthErrorMessage from '../components/auth/AuthErrorMessage';

export default function LandingPage() {
  const navigate = useNavigate();
  const { accounts } = useMsal();

  const {
    isAuthenticating,
    error: authError,
    handleLogin,
    handleLogout,
  } = useAuth();

  // Resolve display name from MSAL account
  const userName = accounts[0]?.name || accounts[0]?.username || undefined;

  // Handle post-redirect auth: process MSAL redirect response on mount
  useEffect(() => {
    const handlePostRedirect = async () => {
      try {
        await initializeMsal();
        const result = await msalInstance.handleRedirectPromise();
        if (result) {
          // User just returned from loginRedirect — check access and route
          const access = await handleLogin();
          if (access?.allowed) {
            navigate(
              access.route === 'setup-required' ? '/onboarding' : '/dashboard',
            );
          }
        }
      } catch {
        // Redirect processing failed silently — user can click Sign In again
      }
    };
    handlePostRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginClick = async () => {
    try {
      const result = await handleLogin();
      if (result.allowed) {
        // route tells us where to go: 'dashboard' for returning users, 'setup-required' for new users
        navigate(
          result.route === 'setup-required' ? '/onboarding' : '/dashboard',
        );
      }
    } catch (error) {
      // Error handled by useAuth
    }
  };

  // Show login button only if user is not authenticated
  const showLoginButton = !isAuthenticated();

  const handleLogoutClick = async () => {
    await handleLogout();
    navigate('/');
  };

  return (
    <div className='min-h-screen flex flex-col bg-[#F3F4F6]'>
      <Header
        isAuthenticated={isAuthenticated()}
        userName={userName}
        onSignIn={handleLoginClick}
        onSignOut={handleLogoutClick}
      />
      <Hero />
      <Features />

      <div>
        <CallToAction>
          {/* Auth error */}
          {authError && (
            <div className='mt-4 max-w-2xl mx-auto'>
              <AuthErrorMessage
                message={authError}
                onRetry={handleLoginClick}
              />
            </div>
          )}

          {/* Login button for unauthenticated users */}
          {showLoginButton && (
            <div className='mt-6 max-w-md mx-auto'>
              <AuthButton
                onClick={handleLoginClick}
                isLoading={isAuthenticating}
                label='Sign In'
              />
            </div>
          )}

          {/* Dashboard button for authenticated users */}
          {isAuthenticated() && (
            <div className='mt-6 max-w-md mx-auto'>
              <button
                onClick={() => navigate('/dashboard')}
                className='w-full py-4 px-6 bg-[#2ba6bb] text-white rounded-lg hover:bg-[#228a9d] transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl'
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </CallToAction>
      </div>

      <Footer />
    </div>
  );
}
